import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';
import Notifications from './Notifications';
import MessagesInbox from './MessagesInbox';
import AuthModal from './AuthModal';

function Nav({ currentUser, currentUserProfile, onSignOut }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [authOpen, setAuthOpen] = useState(false);
  const menuRef = useRef();
  const navigate = useNavigate();

  // Close menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Real-time unread notification count
  useEffect(() => {
    if (!currentUser?.uid) return;
    const q = query(
      collection(db, 'users', currentUser.uid, 'notifications'),
      where('read', '==', false)
    );
    const unsub = onSnapshot(q, snap => setUnreadCount(snap.size));
    return unsub;
  }, [currentUser?.uid]);

  const handleSignOut = async () => {
    await signOut(auth);
    // In the Android app, also drop the native session so sign-out returns to the
    // native login screen (where native Google works) instead of a logged-out web
    // view that dead-ends at web Google sign-in. No-op on the website.
    try { window.YT8Native?.signOut?.(); } catch { /* not in the app */ }
    setMenuOpen(false);
    if (onSignOut) onSignOut();
    navigate('/');
  };

  // App's onAuthStateChanged listener picks up the session after the modal signs in.

  const avatarSrc = currentUserProfile?.photoURL || currentUser?.photoURL;
  const initials = (currentUserProfile?.displayName || currentUser?.displayName || '?')[0].toUpperCase();

  return (
    <>
      <nav className="nav">
        <Link
          to="/"
          className="nav-logo"
        >
          <img src="/yt8.png" alt="Your Top 8" style={{ height: 44, width: 44, objectFit: 'contain' }} />
        </Link>

        <div className="nav-actions">
          {currentUser ? (
            <>
              {/* Find people */}
              <button
                onClick={() => navigate('/people')}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '6px', color: 'var(--text-muted)',
                  display: 'flex', alignItems: 'center',
                }}
                title="Find people"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>

              {/* Notifications bell */}
              <button
                onClick={() => setNotifOpen(prev => !prev)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  position: 'relative',
                  padding: '6px',
                  color: notifOpen ? 'var(--primary)' : 'var(--text-muted)',
                  fontSize: 18,
                  display: 'flex',
                  alignItems: 'center',
                }}
                title="Notifications"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {unreadCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      background: 'var(--primary)',
                      color: '#0d0d0d',
                      fontSize: 9,
                      fontWeight: 800,
                      borderRadius: 999,
                      minWidth: 16,
                      height: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 3px',
                      lineHeight: 1,
                    }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Admin messages inbox (only renders when a conversation is open) */}
              <MessagesInbox currentUser={currentUser} />

              {/* Avatar + dropdown */}
              <div ref={menuRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setMenuOpen(prev => !prev)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {avatarSrc ? (
                    <img className="nav-avatar" src={avatarSrc} alt="profile" />
                  ) : (
                    <div
                      className="nav-avatar"
                      style={{
                        width: 34,
                        height: 34,
                        background: 'var(--surface3)',
                        color: 'var(--primary)',
                        fontSize: 14,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {initials}
                    </div>
                  )}
                </button>

                {menuOpen && (
                  <div
                    className="nav-dropdown"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      right: 0,
                      background: 'var(--card)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-lg)',
                      minWidth: 200,
                      boxShadow: 'var(--shadow)',
                      overflow: 'hidden',
                      zIndex: 200,
                      animation: 'slideUp 0.15s ease',
                    }}
                  >
                    {currentUserProfile && (
                      <div
                        style={{
                          padding: '14px 16px',
                          borderBottom: '1px solid var(--border)',
                        }}
                      >
                        <div style={{ fontWeight: 700, fontSize: 14 }}>
                          {currentUserProfile.displayName}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--text-muted)',
                            marginTop: 2,
                          }}
                        >
                          @{currentUserProfile.username}
                        </div>
                      </div>
                    )}

                    <div style={{ padding: '6px 0' }}>
                      {currentUserProfile && (
                        <NavDropItem
                          to={`/${currentUserProfile.username}`}
                          onClick={() => setMenuOpen(false)}
                        >
                          My Profile
                        </NavDropItem>
                      )}
                      <NavDropItem to="/people" onClick={() => setMenuOpen(false)}>
                        Find People
                      </NavDropItem>
                      <NavDropItem to="/help" onClick={() => setMenuOpen(false)}>
                        Help &amp; FAQ
                      </NavDropItem>
                      <NavDropItem to="/settings" onClick={() => setMenuOpen(false)}>
                        Settings
                      </NavDropItem>
                      <hr className="divider" style={{ margin: '6px 0' }} />
                      <button
                        onClick={handleSignOut}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '8px 16px',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 13,
                          color: 'var(--bad)',
                          fontFamily: 'var(--font)',
                        }}
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <button className="btn btn-primary btn-sm nav-signin" onClick={() => setAuthOpen(true)} title="Sign in or sign up">
              <span className="nav-signin-label">Sign In</span>
              <svg className="nav-signin-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
            </button>
          )}
        </div>
      </nav>

      {/* Notification panel */}
      {notifOpen && (
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 490,
              background: 'transparent',
            }}
            onClick={() => setNotifOpen(false)}
          />
          <Notifications onClose={() => setNotifOpen(false)} currentUserProfile={currentUserProfile} />
        </>
      )}

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
}

function NavDropItem({ to, onClick, children, state }) {
  return (
    <Link
      to={to}
      state={state}
      onClick={onClick}
      style={{
        display: 'block',
        padding: '9px 16px',
        fontSize: 14,
        color: 'var(--text)',
        textDecoration: 'none',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      {children}
    </Link>
  );
}

export default Nav;
